import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormHelperText,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { Map } from "schemas/functions";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import ReviewBentoAiTemplate from "features/Influencer/HomePage/CreateTemplateDialog/ReviewBentoAiTemplate";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

interface Props {
  handleBack: () => void;
  handleNext: () => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  setOnboardingForm: React.Dispatch<React.SetStateAction<Map>>;
}

const loadingMessages = [
  "Hold tight, your personalized template is on its way...",
  "Formatting your outreach with the right variables...",
  "Almost done, just adding the finishing touches...",
];

const EditTemplateFromScratch = ({
  handleBack,
  handleNext,
  handleSkip,
}: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const {
    fetchOutreachTemplates,
    outreachTemplates,
    followupTemplates,
    setTemplates,
  } = useContext(OutreachTemplatesContext);
  const [editTemplate, setEditTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.OUTREACH,
    name: "My Outreach",
    signature: profile?.signature,
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
    name: "My Follow-Up",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [message, setMessage] = useState(loadingMessages[0]);

  const [open, setOpen] = useState(false);

  const saveTemplate = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}`,
        "PUT",
        {},
        { ...editTemplate },
      );
      handleNext();
      setOpen(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const createTemplate = async () => {
    if (!currentOrg?.id) return;
    try {
      setCreateLoading(true);
      const res = await fetcherAuth(
        getToken,
        editTemplate?.id
          ? `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}`
          : `/api/organization/${currentOrg?.id}/templates`,
        editTemplate?.id ? "PUT" : "POST",
        {},
        { ...editTemplate, fromOnboarding: true },
      );
      setEditTemplate(res.template);
      _updateLocalTemplate(res.template);
      if (showFollowUp && followupTemplate?.body) {
        await createFollowUp();
      }
      if (!profile?.reviewedFirstTemplate) {
        setOpen(true);
      } else {
        handleNext();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const createFollowUp = async () => {
    try {
      setCreateLoading(true);
      const res = await fetcherAuth(
        getToken,
        editTemplate?.id
          ? `/api/organization/${currentOrg?.id}/templates/${followupTemplate?.id}`
          : `/api/organization/${currentOrg?.id}/templates`,
        editTemplate?.id ? "PUT" : "POST",
        {},
        { ...followupTemplate, fromOnboarding: true },
      );
      setFollowupTemplate(res.template);
      _updateLocalTemplate(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const _updateLocalTemplate = (template: Template) => {
    setTemplates((prevTemplates) => {
      const updatedTemplates = makeDeepCopy(prevTemplates);
      if (template.id) {
        const index = updatedTemplates.findIndex(
          (t: Template) => t.id === template.id,
        );
        if (index !== -1) {
          updatedTemplates[index] = template;
        }
        return updatedTemplates;
      }
    });
  };

  const initializeTemplates = () => {
    if (outreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    } else {
      const existingTemplate = outreachTemplates?.[0];
      if (existingTemplate) {
        setEditTemplate(existingTemplate);
      }

      if (followupTemplates && followupTemplates?.length > 0) {
        const existingFollowUp = followupTemplates[0];
        if (existingFollowUp && !followupTemplate?.id) {
          setFollowupTemplate(existingFollowUp);
        }
        setShowFollowUp(true);
      }
    }
  };

  useEffect(() => {
    initializeTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachTemplates?.length, followupTemplates?.length]);

  useEffect(() => {
    let index = 0;
    if (createLoading) {
      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setMessage(loadingMessages[index]);
      }, 2000);
      return () => clearInterval(interval); // cleanup
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createLoading]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-marker"
        />
        {currentOrg?.name}'s First Pitch
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        alignItems={"center"}
        flexDirection="column"
      >
        <Box sx={{ width: 600 }}>
          {createLoading && (
            <>
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton
                animation="wave"
                width="100%"
                height="300px"
                sx={{ mt: -7, mb: -8 }}
              />
              <Skeleton animation="wave" width="100%" height="80px" />

              <Grid container justifyContent="center">
                <CircularProgress size={20} sx={{ mr: 2 }} />
                {message}
              </Grid>
            </>
          )}

          {!createLoading && (
            <TemplateBody
              editTemplate={editTemplate}
              setEditTemplate={setEditTemplate}
              isNew={!(outreachTemplates?.length > 0)}
              allowChangeTitle={false}
              minimalist
              displayError={true}
              displaySignature={true}
            />
          )}
        </Box>
        {!showFollowUp && !createLoading && (
          <Button
            size="small"
            sx={{ textTransform: "none" }}
            onClick={() => setShowFollowUp(true)}
          >
            Add Follow-Up
          </Button>
        )}

        {showFollowUp && !createLoading && (
          <Box sx={{ width: 600, mt: 2 }}>
            <Grid container justifyContent={"space-between"}>
              <FormHelperText>Follow-up (optional):</FormHelperText>
              <Tooltip title="Remove follow-up">
                <IconButton
                  sx={{ fontSize: 12 }}
                  onClick={() => setShowFollowUp(false)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </Tooltip>
            </Grid>

            <TemplateBody
              editTemplate={followupTemplate}
              setEditTemplate={setFollowupTemplate}
              isNew={true}
              allowChangeTitle={false}
              minimalist
              displayError={true}
            />
          </Box>
        )}
      </Box>

      <Grid item xs="auto" container columnGap={1}>
        <Grid item xs="auto">
          <Button
            disabled={createLoading}
            onClick={handleBack}
            color="secondary"
            disableElevation
          >
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>

        {handleSkip && (
          <Grid item xs="auto">
            <Button onClick={handleSkip} disableElevation color="secondary">
              Skip
            </Button>
          </Grid>
        )}

        <Grid item xs="auto">
          <LoadingButton
            disableElevation
            variant="contained"
            type="submit"
            disabled={
              !editTemplate.subject || !editTemplate.body || createLoading
            }
            onClick={createTemplate}
            loading={createLoading}
          >
            Next
          </LoadingButton>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleNext} maxWidth="md" fullWidth>
        <DialogContent>
          <ReviewBentoAiTemplate
            editTemplate={editTemplate}
            setEditTemplate={setEditTemplate}
            handleBack={handleBack}
            handleNext={saveTemplate}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditTemplateFromScratch;
